import logoOriginal from "@/assets/lorier-logo.svg";
import logoIcon from "@/assets/lorier-logo-icon.svg";
import { cn } from "@/lib/utils";
import { panelAtom } from "@/stores/atoms/panel-atom";
import { useAtomValue } from "jotai";
import { LogOut } from "lucide-react";
import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";
import { ROUTES } from "@/constants/routes";
import Icon from "./icon";
import UserProfileDropdownButton from "./user-profile-dropdown-button";
import { useActiveNavHref, useSideNavigationLinks } from "@/hooks";
import useAuth from "@/features/auth/hooks/useAuth";
import { IconNavSettings } from "./icons";

const SideNavigation = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigationLinks = useSideNavigationLinks();
  const showPanel = useAtomValue(panelAtom);
  const activeHref = useActiveNavHref();
  return (
    <div
      className={cn(
        "flex-1 hidden sm:block z-30",
        showPanel ? "max-w-64" : "max-w-16",
      )}
    >
      {showPanel ? (
        <aside className="flex flex-col h-full bg-white border-r border-gray-200">
          <div className="h-17.25 flex items-center mt-4 mb-3">
            <img src={logoOriginal} alt="Lorier Logo" className="w-20 mx-5" />
          </div>
          <nav className="flex flex-col gap-y-2">
            {navigationLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className={cn(
                  "flex items-center gap-2 bg px-4 py-2 border-r-4",
                  activeHref === link.href
                    ? "bg-primary/10 font-semibold text-primary border-primary"
                    : "border-transparent text-gray-500 hover:border-primary hover:text-primary",
                )}
              >
                <Icon icon={link.icon} />
                <span>{link.label}</span>
              </Link>
            ))}
          </nav>
          <div className="mt-auto">
            <div
              className={cn(
                "border-y h-14",
                activeHref === ROUTES.SETTINGS && "border-y-transparent",
              )}
            >
              <Link
                to={ROUTES.SETTINGS}
                className={cn(
                  "flex items-center gap-3 h-full px-5 py-4 border-r-4 border-r-transparent text-gray-500 hover:border-r-primary hover:text-primary",
                  activeHref === ROUTES.SETTINGS &&
                    "font-semibold text-primary border-primary bg-primary/10",
                )}
              >
                <IconNavSettings />
                <span>{t("keys.settings")}</span>
              </Link>
            </div>
            <div className="px-4 h-[83px] flex items-center justify-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="focus:outline-0 bg-gray-50 hover:bg-primary/5 w-full cursor-pointer p-2 rounded-lg flex items-center justify-between">
                    <UserProfileDropdownButton />
                  </button>
                </DropdownMenuTrigger>
                <ProfileDropdownContent />
              </DropdownMenu>
            </div>
          </div>
        </aside>
      ) : (
        <aside className="flex flex-col h-full bg-white border-r border-gray-200">
          <div className="h-17.25 flex items-center mb-3 justify-center">
            <img src={logoIcon} alt="Lorier Logo Icon" className="size-7" />
          </div>
          <nav className="flex flex-col gap-y-2">
            {navigationLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className={cn(
                  "flex items-center justify-center gap-2 bg px-4 py-2 border-x-2",
                  activeHref === link.href
                    ? "bg-primary/10 font-semibold text-primary border-r-primary border-l-transparent"
                    : "border-transparent text-gray-500 hover:border-r-primary hover:text-primary",
                )}
              >
                <Icon icon={link.icon} />
              </Link>
            ))}
          </nav>
          <div className="mt-auto">
            <div
              className={cn(
                "border-y h-14",
                activeHref === ROUTES.SETTINGS && "border-y-transparent",
              )}
            >
              <Link
                to={ROUTES.SETTINGS}
                className={cn(
                  "flex items-center h-full justify-center gap-3 px-4 py-4 border-x-2 border-x-transparent text-gray-500 hover:border-r-primary hover:text-primary",
                  activeHref === ROUTES.SETTINGS &&
                    "font-semibold text-primary border-r-primary bg-primary/10",
                )}
              >
                <IconNavSettings />
              </Link>
            </div>
            <div className="flex items-center justify-center h-[83px]">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="py-2">
                    <div className="size-10 cursor-pointer bg-primary/10 flex items-center justify-center rounded-full">
                      <p className="font-bold uppercase text-sm text-primary">
                        {user?.username[0]}
                        {user?.username[1]}
                      </p>
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <ProfileDropdownContent />
              </DropdownMenu>
            </div>
          </div>
        </aside>
      )}
    </div>
  );
};

const ProfileDropdownContent = () => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  return (
    <DropdownMenuContent side="right" className="-translate-y-4">
      <DropdownMenuGroup>
        <DropdownMenuItem variant="destructive" asChild>
          <button
            onClick={() => logout()}
            className="flex items-center w-full gap-2 text-destructive"
          >
            <LogOut className="text-destructive" />
            {t("keys.logout")}
          </button>
        </DropdownMenuItem>
      </DropdownMenuGroup>
    </DropdownMenuContent>
  );
};

export default SideNavigation;
